import { useState } from "react";
import { Badge } from "~/components/ui/badge";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import useUser from "~/contexts/user";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { SettingsGroup } from "~/pages/manifold-nextgen/components/settings/SettingsGroup";
import { SettingsPage } from "~/pages/manifold-nextgen/components/settings/SettingsPage";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { deleteMemory, mutateMemoryList, type PartialMemory } from "~/services/memories";
import { MemoryDetailDialog } from "./MemoryDetailDialog";
import { MemoryTable } from "./MemoryTable";
import { useMemoriesPreference } from "./useMemoriesPreference";
import { useUserMemories } from "./useUserMemories";

/**
 * The profile's Memories tab: what the agents carry across conversations on this
 * user's behalf. Memories are agent-written, so the user can read and delete
 * them but not edit them.
 *
 * Turning memories off stops the agents reading and writing; it deletes nothing.
 * The table stays listed and deletable at full contrast, so a user who has
 * opted out can still audit and clear what was recorded about them.
 *
 * Off is shown by a badge and a receded card surface rather than by copy that
 * appears and disappears. Every string on the page is fixed, so the layout
 * never shifts under the reader as the preference resolves or is changed.
 */
export const ProfileMemoriesTab = () => {
  const user = useUser();
  const { showSuccess, showError } = useToast();
  const { memories, isLoading, error } = useUserMemories(user !== null);
  const { enabled, isSaving, setEnabled } = useMemoriesPreference();

  const [openMemory, setOpenMemory] = useState<PartialMemory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PartialMemory | null>(null);

  const handleDeleteConfirm = async (memory: PartialMemory) => {
    try {
      await deleteMemory(memory.id);
      await mutateMemoryList();
      showSuccess(`"${memory.name}" forgotten`);
    } catch (e) {
      showError("Unable to delete this memory");
      throw e;
    }
  };

  const handleToggle = async (next: boolean) => {
    try {
      await setEnabled(next);
    } catch {
      showError("Unable to save this setting");
    }
  };

  return (
    <SettingsPage
      title="Memory"
      description="Agents remember details from your conversations and draw on them in later ones."
    >
      <SettingsGroup>
        <SettingsRow
          title="Let agents use memories"
          description="When off, agents stop reading and writing memories. Nothing is deleted."
        >
          <Switch
            checked={enabled ?? false}
            onCheckedChange={handleToggle}
            disabled={enabled === null || isSaving}
            aria-label="Let agents use memories"
          />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup
        label="Saved memories"
        count={memories.length > 0 ? memories.length : undefined}
        badge={enabled === false ? <Badge variant="secondary">Paused</Badge> : undefined}
        inactive={enabled === false}
      >
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : error ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-destructive">Unable to load your memories.</p>
          </div>
        ) : memories.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">Nothing remembered yet.</p>
          </div>
        ) : (
          <MemoryTable memories={memories} onOpen={setOpenMemory} onDelete={setDeleteTarget} />
        )}
      </SettingsGroup>

      <MemoryDetailDialog memory={openMemory} onClose={() => setOpenMemory(null)} />

      <ConfirmDestructiveDialog
        target={deleteTarget}
        title="Delete this memory?"
        description={(memory) => (
          <>
            &ldquo;{memory.name}&rdquo; will be permanently deleted. The agents will stop using it
            in your conversations.
          </>
        )}
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteTarget(null)}
      />
    </SettingsPage>
  );
};
